import { useState, useRef } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import {
  ScanSearch, Upload, Camera, X, CheckCircle, AlertTriangle,
  Smartphone, Wrench, Info, Loader, PencilLine, Save, Plus,
} from "lucide-react";

function compress(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) { reject("not_image"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1280;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject("img_err");
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhoneIdentifier() {
  const [images, setImages] = useState([]); // array of data URLs
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(false);
  const [correctBrand, setCorrectBrand] = useState("");
  const [correctModel, setCorrectModel] = useState("");
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) {
      if (images.length >= 4) { toast.warning("Máximo 4 fotos"); break; }
      try {
        const data = await compress(f);
        setImages((arr) => arr.length < 4 ? [...arr, data] : arr);
      } catch { toast.error("Arquivo inválido"); }
    }
    setResult(null); setErr(""); setEditing(false);
  };

  const remove = (i) => setImages((arr) => arr.filter((_, idx) => idx !== i));

  const clear = () => {
    setImages([]); setResult(null); setErr(""); setNotes(""); setEditing(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const identify = async () => {
    if (images.length === 0) { toast.error("Envie ao menos uma foto"); return; }
    setLoading(true); setErr(""); setResult(null); setEditing(false);
    try {
      const { data } = await api.post("/identify-phone", {
        images_base64: images, mime_type: "image/jpeg", notes,
      }, { timeout: 90000 });
      setResult(data);
      setCorrectBrand(data.brand || "");
      setCorrectModel(data.model || "");
      if (data.confidence === "alta") toast.success(`${data.brand} ${data.model}`);
      else toast.warning("Identificação incerta. Veja alternativas.");
    } catch (e) {
      const msg = e.response?.data?.detail
        ? formatErr(e.response.data.detail)
        : e.code === "ECONNABORTED" ? "A IA demorou demais. Tente com menos fotos."
        : e.message || "Erro ao identificar.";
      setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const saveCorrection = async () => {
    if (!correctBrand.trim() || !correctModel.trim()) { toast.error("Preencha marca e modelo"); return; }
    try {
      await api.post("/identify-phone/correct", {
        identification_id: result.identification_id,
        correct_brand: correctBrand.trim(),
        correct_model: correctModel.trim(),
        user_note: notes,
      });
      setResult({ ...result, brand: correctBrand, model: correctModel, corrected: true });
      setEditing(false);
      toast.success("Correção salva! Obrigado pelo feedback.");
    } catch { toast.error("Erro ao salvar"); }
  };

  const confBadge = (c) => c === "alta" ? "bg-emerald-100 text-emerald-700"
    : c === "media" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

  return (
    <div className="space-y-6" data-testid="phone-identifier-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Assistência Técnica</div>
        <h1 className="font-display text-4xl font-bold">Identificar Aparelho</h1>
        <p className="text-slate-500 text-sm mt-1">
          Envie <strong>até 4 fotos</strong> (traseira + base + frente) para melhor precisão. A IA identifica marca e modelo.
        </p>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upload */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ScanSearch size={18} className="text-[#2563EB]" /> Fotos do aparelho
            <span className="ml-auto text-xs text-slate-500">{images.length}/4</span>
          </h2>

          {images.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-square bg-slate-50 border border-slate-200 rounded-sm overflow-hidden">
                  <img src={src} alt={`foto ${i + 1}`} className="w-full h-full object-cover" data-testid={`pi-img-${i}`} />
                  <button onClick={() => remove(i)} data-testid={`pi-remove-${i}`} className="absolute top-1 right-1 bg-white/90 border border-slate-300 rounded-sm p-0.5 hover:bg-red-50">
                    <X size={12} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {images.length < 4 && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => inputRef.current?.click()} data-testid="pi-upload-btn"
                className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center hover:border-[#2563EB] hover:bg-blue-50 transition-colors text-slate-500 hover:text-[#2563EB]"
              >
                <Upload size={28} />
                <span className="mt-2 text-sm font-semibold">Enviar foto</span>
                <span className="text-xs opacity-70">Seleção múltipla</span>
              </button>
              <button
                onClick={() => cameraRef.current?.click()} data-testid="pi-camera-btn"
                className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center hover:border-[#2563EB] hover:bg-blue-50 transition-colors text-slate-500 hover:text-[#2563EB]"
              >
                <Camera size={28} />
                <span className="mt-2 text-sm font-semibold">Tirar foto</span>
                <span className="text-xs opacity-70">Câmera do aparelho</span>
              </button>
            </div>
          )}

          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="pi-file-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="pi-camera-input" />

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
            <strong>💡 Dica para iPhones Pro:</strong> foto da <strong>traseira</strong> (câmera) + <strong>base</strong> (ver USB-C ou Lightning) + <strong>lateral</strong> (botão Ação/Controle da Câmera) aumenta muito a precisão.
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Observações (opcional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: aparelho não liga, tela verde, cliente João"
              data-testid="pi-notes"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>

          <div className="flex gap-2">
            <button onClick={identify} disabled={images.length === 0 || loading} data-testid="pi-identify-btn"
              className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-sm flex items-center justify-center gap-2">
              {loading ? <><Loader className="animate-spin" size={16} /> Analisando...</> : <><ScanSearch size={16} /> Identificar com IA</>}
            </button>
            {images.length > 0 && (
              <button onClick={clear} data-testid="pi-clear-btn" className="px-4 py-3 border border-slate-300 rounded-sm text-sm text-slate-600 hover:bg-slate-50">
                Limpar
              </button>
            )}
          </div>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="pi-error">{err}</div>}
        </div>

        {/* Result */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-4">
            <CheckCircle size={18} className="text-emerald-500" /> Identificação
          </h2>

          {!result ? (
            <div className="text-center py-12 text-slate-400">
              <Smartphone size={48} className="mx-auto mb-3" />
              <div className="text-sm">Envie foto(s) do aparelho para começar</div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="pi-result">
              <div className="bg-[#0A1628] text-white rounded-sm p-5 relative">
                {editing ? (
                  <div className="space-y-2">
                    <div className="text-xs uppercase tracking-[0.3em] text-blue-300 font-semibold">Correção Manual</div>
                    <input type="text" value={correctBrand} onChange={(e) => setCorrectBrand(e.target.value)}
                      placeholder="Marca"
                      data-testid="pi-correct-brand"
                      className="w-full px-3 py-2 rounded-sm text-slate-900 text-sm" />
                    <input type="text" value={correctModel} onChange={(e) => setCorrectModel(e.target.value)}
                      placeholder="Modelo exato"
                      data-testid="pi-correct-model"
                      className="w-full px-3 py-2 rounded-sm text-slate-900 text-sm" />
                    <div className="flex gap-2 pt-1">
                      <button onClick={saveCorrection} data-testid="pi-save-correction"
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold py-2 rounded-sm flex items-center justify-center gap-1">
                        <Save size={12} /> Salvar Correção
                      </button>
                      <button onClick={() => setEditing(false)} className="px-3 py-2 border border-white/30 text-xs rounded-sm hover:bg-white/10">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button onClick={() => setEditing(true)} data-testid="pi-edit-btn"
                      className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 p-1.5 rounded-sm" title="Corrigir">
                      <PencilLine size={14} />
                    </button>
                    <div className="text-xs uppercase tracking-[0.3em] text-blue-300 font-semibold">{result.brand}</div>
                    <div className="font-display font-bold text-2xl mt-1">{result.model}</div>
                    {result.corrected && <div className="text-[10px] uppercase tracking-wider text-emerald-300 mt-1">✓ Corrigido manualmente</div>}
                    <div className="text-sm text-slate-300 mt-2">{result.summary}</div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {result.year && <span className="text-xs bg-white/10 text-white uppercase tracking-wider px-2 py-1 rounded-sm">{result.year}</span>}
                      {result.confidence && <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-sm font-semibold ${confBadge(result.confidence)}`}>Confiança: {result.confidence}</span>}
                    </div>
                  </>
                )}
              </div>

              {result.alternatives && !editing && (
                <div className="border border-amber-200 bg-amber-50 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-amber-600" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-amber-800">Modelos Possíveis</span>
                  </div>
                  <div className="text-sm text-amber-900">{result.alternatives}</div>
                  {result.request_more && (
                    <div className="text-xs mt-2 text-amber-800">
                      <Camera size={11} className="inline mr-1" /> Para confirmar: <em>{result.request_more}</em>
                    </div>
                  )}
                </div>
              )}

              {result.visual_clues && !editing && (
                <div className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Info size={14} className="text-[#2563EB]" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">O que a IA viu</span>
                  </div>
                  <p className="text-sm text-slate-700">{result.visual_clues}</p>
                </div>
              )}

              {result.common_issues && !editing && (
                <div className="border border-amber-200 bg-amber-50 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-amber-600" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-amber-800">Problemas comuns</span>
                  </div>
                  <div className="text-sm text-amber-900">
                    {String(result.common_issues).split(";").map((p, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{p.trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.repair_notes && !editing && (
                <div className="border border-blue-200 bg-blue-50 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench size={14} className="text-blue-600" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-blue-800">Dicas de reparo</span>
                  </div>
                  <p className="text-sm text-blue-900">{result.repair_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
